import {
  CreateCartMutation,
  CreateCartMutationVariables,
  SetBuyerIdentityMutation,
  SetBuyerIdentityMutationVariables,
} from "./shopify-storefront.graphql";
import { ShopifyStorefrontClient } from "./storefront-client";

const createCartMutation = /* GraphQL */ `
  mutation CreateCart($input: CartInput) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const setBuyerIdentityMutation = /* GraphQL */ `
  mutation SetBuyerIdentity(
    $cartId: ID!
    $buyerIdentity: CartBuyerIdentityInput!
  ) {
    cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
      userErrors {
        field
        message
      }
    }
  }
`;
//
export async function createCart(
  client: ShopifyStorefrontClient,
  buyerIdentity: SetBuyerIdentityMutationVariables["buyerIdentity"],
  input?: CreateCartMutationVariables["input"],
): Promise<{ id: string; checkoutUrl: string }> {
  const { data: cartCreateData, errors: cartCreateErrors } =
    await client.request<CreateCartMutation, CreateCartMutationVariables>(
      createCartMutation,
      { input },
    );

  if (cartCreateErrors) {
    throw cartCreateErrors;
  }

  if (cartCreateData?.cartCreate?.userErrors?.length) {
    throw cartCreateData.cartCreate.userErrors;
  }

  if (!cartCreateData?.cartCreate?.cart) {
    throw new Error("Cart not created");
  }
  const cart = cartCreateData.cartCreate.cart;

  const { data, errors } = await client.request<
    SetBuyerIdentityMutation,
    SetBuyerIdentityMutationVariables
  >(setBuyerIdentityMutation, { cartId: cart.id, buyerIdentity });
  const userErrors = data?.cartBuyerIdentityUpdate?.userErrors;
  if (userErrors && userErrors.length > 0) {
    console.error("Error setting buyer identity", errors);
  }
  return cart;
}

export async function createCartSimple(
  client: ShopifyStorefrontClient,
  input?: CreateCartMutationVariables["input"],
): Promise<{ id: string; checkoutUrl: string }> {
  const { data, errors } = await client.request<
    CreateCartMutation,
    CreateCartMutationVariables
  >(createCartMutation, { input });

  if (errors) {
    throw errors;
  }

  if (data?.cartCreate?.userErrors?.length) {
    console.error("cartCreate returned userErrors", {
      userErrors: data.cartCreate.userErrors,
      input,
    });
    throw data.cartCreate.userErrors;
  }

  if (!data?.cartCreate?.cart) {
    throw new Error("Cart not created");
  }
  const cart = data.cartCreate.cart;

  return cart;
}

export interface SubscriptionCartLine {
  variantGid: string;
  sellingPlanGid: string;
  quantity: number;
}

/**
 * Creates a Shopify Storefront cart with one or more subscription line items
 * and returns its hosted checkout URL plus the cart's Storefront global ID.
 *
 * The cart ID is needed by callers that want to attach discount codes to the
 * cart server-side via `cartDiscountCodesUpdate` — the only reliable way to
 * auto-apply a discount on a Storefront-API hosted checkout.
 *
 * Returns null on any error (no lines, userErrors, GraphQL failure) so
 * callers can fall back to a non-subscription path instead of sending a
 * broken link.
 */
export async function createSubscriptionCheckoutUrl(
  client: ShopifyStorefrontClient,
  lines: SubscriptionCartLine[],
): Promise<{ cartId: string; checkoutUrl: string } | null> {
  if (lines.length === 0) {
    return null;
  }
  try {
    const cart = await createCartSimple(client, {
      lines: lines.map((line) => ({
        merchandiseId: line.variantGid,
        quantity: line.quantity,
        sellingPlanId: line.sellingPlanGid,
      })),
    });
    return { cartId: cart.id, checkoutUrl: cart.checkoutUrl };
  } catch (error) {
    console.warn("createSubscriptionCheckoutUrl failed", { error, lines });
    return null;
  }
}
